'use client';

import { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteAuthToken, getAuthToken, setAuthToken } from '../_lib/cookies';
import { fetchUserInfoAction } from '../_actions';
import { decodeJWT } from '../_lib/jwt-utils';
import type { UserInfo, JWTPayload, AuthContextType } from '../_types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = getAuthToken();

    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    const result = await fetchUserInfoAction();

    if (result.success && result.data) setUser(result.data);
    else {
      deleteAuthToken();
      setUser(null);
    }

    setIsLoading(false);
  }, []);

  const login = useCallback(
    async (token: string) => {
      setAuthToken(token);
      await fetchUser();
    },
    [fetchUser],
  );

  const logout = useCallback(() => {
    deleteAuthToken();
    setUser(null);
    navigate('/auth/login', { replace: true });
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Listen for expired key events and logout
  useEffect(() => {
    const handleAuthExpired = () => {
      logout();
    };

    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, [logout]);

  const apiKey = (() => {
    const token = getAuthToken();
    if (!token) return null;
    const decoded = decodeJWT<JWTPayload>(token);
    return decoded?.key || null;
  })();

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    apiKey,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
